import { createBrowserRouter, type LoaderFunctionArgs, Outlet, redirect } from "react-router";

import { authStore } from "@/lib/auth/auth";
import { AuthProvider } from "@/lib/auth/auth-provider";

import { DashboardLayout } from "./dashboard-layout";
import { NotFound } from "./not-found";
import { AppLoading, PageLoading } from "./page-loading";
import { DashboardError } from "./route-error";

/**
 * Dashboard routes need a session the API still accepts (checked once per page load); the original
 * location comes back as `?next=`.
 */
async function requireSession({ request }: LoaderFunctionArgs) {
  const state = await authStore.verify();
  if (state.status === "signed-in") return null;
  const url = new URL(request.url);
  const next = `${url.pathname}${url.search}`;
  const params = new URLSearchParams();
  if (next !== "/") params.set("next", next);
  if (state.reason === "expired") params.set("reason", "expired");
  const query = params.toString();
  throw redirect(query ? `/login?${query}` : "/login");
}

function guestOnly() {
  return authStore.getState().status === "signed-in" ? redirect("/") : null;
}

/** Settings used to be one page of tabs (`/settings?tab=areas`); each tab is its own page now. */
const SETTINGS_TAB_PAGES: Record<string, string> = {
  company: "/settings/company",
  areas: "/settings/service-areas",
  slots: "/settings/delivery-slots",
  lab: "/lab-results",
};

function settingsRedirect({ request }: LoaderFunctionArgs) {
  const tab = new URL(request.url).searchParams.get("tab") ?? "";
  return redirect(SETTINGS_TAB_PAGES[tab] ?? "/settings/store");
}

/** The root route: everything below can use `useAuth()`. */
function AuthRoot() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    Component: AuthRoot,
    hydrateFallbackElement: <AppLoading />,
    children: [
      { path: "/login", loader: guestOnly, lazy: () => import("@/pages/login") },
      {
        id: "dashboard",
        loader: requireSession,
        Component: DashboardLayout,
        ErrorBoundary: DashboardError,
        hydrateFallbackElement: <PageLoading />,
        children: [
          { index: true, lazy: () => import("@/pages/overview") },
          { path: "orders", lazy: () => import("@/pages/orders") },
          { path: "orders/:id", lazy: () => import("@/pages/order-detail") },
          { path: "products", lazy: () => import("@/pages/products") },
          { path: "products/new", lazy: () => import("@/pages/product-new") },
          { path: "products/:id", lazy: () => import("@/pages/product-detail") },
          { path: "products/:id/edit", lazy: () => import("@/pages/product-edit") },
          { path: "categories", lazy: () => import("@/pages/categories") },
          { path: "farmers", lazy: () => import("@/pages/farmers") },
          { path: "reviews", lazy: () => import("@/pages/reviews") },
          { path: "media", lazy: () => import("@/pages/media") },
          { path: "inventory", lazy: () => import("@/pages/inventory") },
          { path: "customers", lazy: () => import("@/pages/customers") },
          { path: "customers/:id", lazy: () => import("@/pages/customer-detail") },
          { path: "deliveries", lazy: () => import("@/pages/deliveries") },
          { path: "live", lazy: () => import("@/pages/live") },
          { path: "subscriptions", lazy: () => import("@/pages/subscriptions") },
          { path: "packing", lazy: () => import("@/pages/packing") },
          { path: "riders", lazy: () => import("@/pages/riders") },
          { path: "rider-map", lazy: () => import("@/pages/rider-map") },
          { path: "coupons", lazy: () => import("@/pages/coupons") },
          { path: "banners", lazy: () => import("@/pages/banners") },
          { path: "home-page", lazy: () => import("@/pages/home-page") },
          { path: "recommendations", lazy: () => import("@/pages/recommendations") },
          { path: "announcements", lazy: () => import("@/pages/announcements") },
          { path: "notifications", lazy: () => import("@/pages/notifications") },
          { path: "refunds", lazy: () => import("@/pages/refunds") },
          { path: "wallet", lazy: () => import("@/pages/wallet") },
          { path: "invoices", lazy: () => import("@/pages/invoices") },
          { path: "invoices/:id", lazy: () => import("@/pages/invoice-detail") },
          { path: "support", lazy: () => import("@/pages/support") },
          { path: "support/:id", lazy: () => import("@/pages/ticket-detail") },
          { path: "help-faqs", lazy: () => import("@/pages/help-faqs") },
          { path: "settings", loader: settingsRedirect },
          { path: "settings/store", lazy: () => import("@/pages/settings-store") },
          { path: "settings/company", lazy: () => import("@/pages/settings-company") },
          { path: "settings/service-areas", lazy: () => import("@/pages/settings-service-areas") },
          {
            path: "settings/delivery-slots",
            lazy: () => import("@/pages/settings-delivery-slots"),
          },
          { path: "lab-results", lazy: () => import("@/pages/lab-results") },
        ],
      },
      { path: "*", Component: NotFound },
    ],
  },
]);
